import { LinkThreeData } from "../../../../types";
import linksthree from "../../../../services/linksthree";
import { WIZARD_DELAYS } from "../constants";
import { BaseStepDependencies } from "./stepTypes";

interface ProfileStepDependencies extends BaseStepDependencies {
  profileData: LinkThreeData | null;
  setProfileData: (data: LinkThreeData) => void;
  addUserMessage: (content: string) => void;
}

// Estado temporário para acumular os dados do perfil durante o wizard
const profileAccumulator: {
  name?: string;
  photo_base64?: string;
  description?: string;
} = {};

export const handleProfileName = (
  userInput: string,
  deps: ProfileStepDependencies,
) => {
  const {
    profileData,
    addBotMessage,
    advanceToStep,
    setProfileData,
    setAwaitingUserInput,
  } = deps;

  // Validações do nome
  const trimmedName = userInput.trim();

  // Validação: nome não pode estar vazio
  if (!trimmedName) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome não pode estar vazio. Por favor, digite seu nome profissional.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: nome deve ter pelo menos 2 caracteres
  if (trimmedName.length < 2) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome deve ter pelo menos 2 caracteres. Por favor, digite um nome válido.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: nome não deve ter mais de 100 caracteres
  if (trimmedName.length > 100) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome não pode ter mais de 100 caracteres. Por favor, use um nome mais curto.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: nome não deve conter apenas números
  if (/^\d+$/.test(trimmedName)) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome não pode conter apenas números. Por favor, digite seu nome profissional.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Salva no acumulador
  profileAccumulator.name = trimmedName;

  if (profileData) {
    setProfileData({ ...profileData, name: trimmedName });
  }

  // Verifica se o usuário já tem foto
  const hasPhoto = profileData?.photo || profileData?.photo_base64;

  if (hasPhoto) {
    // Se já tem foto, pula direto para descrição
    setTimeout(() => {
      addBotMessage(
        `Ótimo, ${trimmedName}! Vi que você já tem uma foto de perfil. Agora, escreva uma breve descrição sobre você e seu trabalho.`,
        { wizardType: "question", delay: 600 },
      );
    }, WIZARD_DELAYS.MEDIUM);

    setTimeout(() => {
      advanceToStep("profile_description", 0);
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
  } else {
    // Se não tem foto, pede upload
    setTimeout(() => {
      addBotMessage(
        `Ótimo, ${trimmedName}! Agora vamos adicionar uma foto ao seu perfil.`,
        { wizardType: "info", delay: 600 },
      );
    }, WIZARD_DELAYS.MEDIUM);

    setTimeout(() => {
      advanceToStep("profile_photo", 0);
      showPhotoUpload(deps);
    }, WIZARD_DELAYS.LONG);
  }
};

export const showPhotoUpload = (deps: ProfileStepDependencies) => {
  const { addBotMessage } = deps;

  setTimeout(() => {
    addBotMessage(
      "Escolha uma foto de perfil profissional. Isso ajuda seus alunos a te reconhecerem!",
      { wizardType: "component", delay: 600 },
    );
  }, 800);
};

export const handleProfilePhoto = (
  photoBase64: string | null,
  deps: ProfileStepDependencies,
) => {
  const {
    profileData,
    addBotMessage,
    advanceToStep,
    setProfileData,
    setAwaitingUserInput,
  } = deps;

  // Salva no acumulador
  if (photoBase64) {
    console.log("✅ Foto selecionada, salvando no profileData e acumulador...");
    profileAccumulator.photo_base64 = photoBase64;
    if (profileData) {
      setProfileData({ ...profileData, photo_base64: photoBase64 });
    }
  } else {
    console.log("⏭️ Foto pulada pelo usuário");
    // Remove do acumulador se estava lá
    delete profileAccumulator.photo_base64;
  }

  const message = photoBase64
    ? "Foto perfeita! Agora, escreva uma breve descrição sobre você e seu trabalho."
    : "Sem problemas! Você pode adicionar uma foto depois. Agora, escreva uma breve descrição sobre você e seu trabalho.";

  setTimeout(() => {
    addBotMessage(message, { wizardType: "question", delay: 600 });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("profile_description", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleProfileDescription = async (
  userInput: string,
  deps: ProfileStepDependencies,
) => {
  const {
    userId,
    profileData,
    addBotMessage,
    setProfileData,
    setIsProcessing,
    setAwaitingUserInput,
  } = deps;

  // Validações da descrição
  const trimmedDescription = userInput.trim();

  // Validação: descrição não pode estar vazia
  if (!trimmedDescription) {
    setTimeout(() => {
      addBotMessage(
        "❌ A descrição não pode estar vazia. Por favor, escreva uma breve descrição sobre você e seu trabalho.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: descrição deve ter pelo menos 10 caracteres
  if (trimmedDescription.length < 10) {
    setTimeout(() => {
      addBotMessage(
        "❌ A descrição deve ter pelo menos 10 caracteres. Por favor, escreva um pouco mais sobre você.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: descrição não deve ter mais de 500 caracteres
  if (trimmedDescription.length > 500) {
    setTimeout(() => {
      addBotMessage(
        "❌ A descrição não pode ter mais de 500 caracteres. Por favor, use uma descrição mais curta.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Salva no acumulador
  profileAccumulator.description = trimmedDescription;

  if (profileData) {
    setProfileData({ ...profileData, description: trimmedDescription });
  }

  setTimeout(() => {
    addBotMessage("Perfeito! Agora vou salvar suas informações...", {
      wizardType: "info",
      delay: 600,
    });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(async () => {
    if (userId && profileData) {
      setIsProcessing(true);

      try {
        // Usa os dados do acumulador para garantir que temos os valores mais recentes
        const body: Record<string, unknown> = {
          name: profileAccumulator.name || profileData.name,
          color: profileData.color,
          color_secondary: profileData.color_secondary,
          background: profileData.background,
          description: profileAccumulator.description || userInput,
        };

        // Adiciona photo_base64 se existir no acumulador ou no profileData
        if (profileAccumulator.photo_base64) {
          console.log(
            "📸 Enviando foto base64 do acumulador para o backend...",
          );
          body.photo_base64 = profileAccumulator.photo_base64;
        } else if (profileData.photo_base64) {
          console.log(
            "📸 Enviando foto base64 do profileData para o backend...",
          );
          body.photo_base64 = profileData.photo_base64;
        } else if (profileData.photo) {
          console.log("📸 Enviando URL da foto existente para o backend...");
          body.photo = profileData.photo;
        } else {
          console.log("⚠️ Nenhuma foto para enviar");
        }

        console.log("📤 Salvando perfil com dados acumulados:", {
          name: body.name,
          description: body.description,
          color: body.color,
          color_secondary: body.color_secondary,
          background: body.background,
          photo_base64: body.photo_base64 ? "[BASE64_DATA]" : undefined,
        });

        const response = await linksthree.putSave(
          userId,
          body as unknown as Partial<LinkThreeData>,
        );

        console.log("✅ Resposta do salvamento:", response);

        if (response) {
          // Limpa o acumulador após salvar com sucesso
          Object.keys(profileAccumulator).forEach((key) => {
            delete profileAccumulator[key as keyof typeof profileAccumulator];
          });

          addBotMessage(
            "Perfil salvo com sucesso! Suas informações estão lindas!",
            { wizardType: "confirmation", delay: 600 },
          );

          setTimeout(() => {
            addBotMessage("Agora vamos configurar seus serviços e planos. 💼", {
              wizardType: "info",
              delay: WIZARD_DELAYS.MEDIUM,
            });
          }, WIZARD_DELAYS.LONG);

          setTimeout(() => {
            proceedToServices(deps);
          }, 2800);
        } else {
          addBotMessage("❌ Ops! Ocorreu um erro ao salvar. Tente novamente.", {
            wizardType: "error",
            delay: 600,
          });
          setTimeout(() => {
            setAwaitingUserInput(true);
          }, WIZARD_DELAYS.LONG);
        }
      } catch (error) {
        console.error("Erro ao salvar perfil:", error);
        addBotMessage("❌ Ops! Ocorreu um erro ao salvar. Tente novamente.", {
          wizardType: "error",
          delay: 600,
        });
        setTimeout(() => {
          setAwaitingUserInput(true);
        }, WIZARD_DELAYS.LONG);
      } finally {
        setIsProcessing(false);
      }
    }
  }, WIZARD_DELAYS.LONG);
};

const proceedToServices = (deps: ProfileStepDependencies) => {
  const { advanceToStep } = deps;

  advanceToStep("services", 0);
  // Importar e chamar o step de serviços
  void import("./servicesStep").then((module) => {
    module.startServicesConfiguration(deps as never);
  });
};

// Removida - não é mais necessária, os steps agora seguem o fluxo automático
